import {
  Field, FieldProps, Form, Formik, FormikProps,
} from 'formik';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

import { ButtonBase, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import UpdateIcon from '@material-ui/icons/Update';

import ActionModal from 'components/ActionModal/ActionModal';
import FormikOnError from 'components/FormikOnError/FormikOnError';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { rescheduleCampaignExtraTouch } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

import CustomTouchDateTimePicker from './CustomTouchDateTimePicker';
import { getMinimumSendAt } from './utils';

const useStyles = makeStyles((theme) => ({
  modalMessage: {
    fontSize: '15px',
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

interface FormValues {
  sendAt: string;
}

interface Props {
  touchTrigger: any;
  campaignEndDate?: string | null;
  onReceiveEditedTouchTrigger: any;
  onClose: any;
}

const RescheduleExtraTouchModal = ({
  touchTrigger,
  campaignEndDate,
  onReceiveEditedTouchTrigger,
  onClose,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;
  const maximumSendAt = campaignEndDate ? moment(campaignEndDate) : null;
  let initialSendAtMoment = moment.max(moment(touchTrigger.sendAt), getMinimumSendAt());

  if (maximumSendAt && initialSendAtMoment.isAfter(maximumSendAt)) {
    initialSendAtMoment = maximumSendAt.clone();
  }

  return (
    <Formik<FormValues>
      initialValues={{
        sendAt: initialSendAtMoment.toISOString(),
      }}
      validationSchema={Yup.object().shape({
        sendAt: Yup.string()
          .required('Send date and time is required.')
          .test(
            'minimum-lead-time',
            'Extra touches must be scheduled at least 2 hours from now.',
            (value) => !!value && moment(value).isSameOrAfter(getMinimumSendAt()),
          )
          .test(
            'campaign-end-date',
            'Extra touches must be scheduled on or before the campaign end date.',
            (value) => !value || !maximumSendAt || moment(value).isSameOrBefore(maximumSendAt),
          ),
      })}
      onSubmit={async (values) => {
        showLoadingIndicatorModal();

        try {
          const { touchTrigger: updatedTouchTrigger } = await rescheduleCampaignExtraTouch(
            touchTrigger._id,
            moment(values.sendAt).toISOString(),
          );

          onReceiveEditedTouchTrigger(updatedTouchTrigger);
          onClose();
          toast.success('Your extra touch has been rescheduled.');
        } catch (err) {
          console.error(err);
          showAPIErrorAlert(err);
        } finally {
          hideLoadingIndicatorModal();
        }
      }}
    >
      {({ handleSubmit, isSubmitting }: FormikProps<FormValues>) => (
        <Form>
          <FormikOnError>
            <ActionModal
              backgroundColor={theme.palette.offWhite.main}
              textColor={theme.palette.darkGray.main}
              icon={<UpdateIcon />}
              onClose={onClose}
              title="Reschedule Extra Touch"
              message={(
                <div className={classes.modalMessage}>
                  <Field name="sendAt">
                    {({ field, form, meta }: FieldProps) => (
                      <CustomTouchDateTimePicker
                        name={field.name}
                        value={field.value}
                        onChange={(value) => form.setFieldValue(field.name, value)}
                        onClose={() => form.setFieldTouched(field.name, true, true)}
                        minDate={getMinimumSendAt()}
                        maxDate={maximumSendAt ?? undefined}
                        error={meta.touched && !!meta.error}
                        helperText={meta.error ?? 'Extra touches must be scheduled at least 2 hours from now.'}
                      />
                    )}
                  </Field>
                </div>
              )}
              buttons={[
                <ButtonBase onClick={onClose} className={classes.cancelButton}>Cancel</ButtonBase>,
                <ButtonBase onClick={() => handleSubmit()} disabled={isSubmitting}>
                  Save
                </ButtonBase>,
              ]}
            />
          </FormikOnError>
        </Form>
      )}
    </Formik>
  );
};

export default RescheduleExtraTouchModal;
